import { RouterLink } from '@angular/router';
import { Component, inject, OnDestroy } from '@angular/core';
import { NgIf, NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';

import { interval } from 'rxjs';
import { MomentModule } from 'ngx-moment';
import { PrimeTemplate } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, switchMap, take } from 'rxjs/operators';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { MagayaConnectionService } from '@qwyk/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthenticationFacade } from '@qwyk/hub/authentication';
import { PortalsTranslationModule } from '@qwyk/portals/translation';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HubQuotationsWithoutRoutingModule } from '@qwyk/hub/quotations';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HubNotificationsWithoutRoutingModule } from '@qwyk/hub/notifications';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HubAdministrationSitesWithoutRoutingModule } from '@qwyk/hub/administration/sites';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HubAdministrationCustomerUsersWithoutRoutingModule } from '@qwyk/hub/administration/customer-users';

import { versions } from '../../../environments/versions';
import { fadeInAnimation } from '../../helpers/reusableAnimations';
import { HelpModalComponent } from '../help-modal/help-modal.component';

@Component({
    selector: 'app-homepage',
    templateUrl: './homepage.component.html',
    styleUrls: ['./homepage.component.scss'],
    animations: [fadeInAnimation],
    standalone: true,
    imports: [
        NgIf,
        NgClass,
        AsyncPipe,
        RouterLink,
        MomentModule,
        TranslatePipe,
        PrimeTemplate,
        MessagesModule,
        NgTemplateOutlet,
        FontAwesomeModule,
        NgCircleProgressModule,
        PortalsTranslationModule,
        HubQuotationsWithoutRoutingModule,
        HubNotificationsWithoutRoutingModule,
        HubAdministrationSitesWithoutRoutingModule,
        HubAdministrationCustomerUsersWithoutRoutingModule,
    ],
})
export class HomepageComponent implements OnDestroy {
    private dialogService = inject(DialogService);
    private authentication = inject(AuthenticationFacade);
    private magayaConnectionService = inject(MagayaConnectionService);

    public today = new Date();
    public expandNews = false;
    public ref: DynamicDialogRef | undefined;
    public versionInfo = versions;

    public user$ = this.authentication.user$;
    public magayaConnectionStatus$ = interval(200).pipe(
        take(1),
        switchMap(() =>
            this.magayaConnectionService.getMagayaConnectionStatus().pipe(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                filter((status: any) =>
                    ['low', 'medium', 'high'].includes(status.severity)
                )
            )
        )
    );

    get greeting(): string {
        const greetings = [
            { from: 0, to: 4, key: 'portal.common.greetings.good-night' },
            { from: 5, to: 11, key: 'portal.common.greetings.good-morning' },
            { from: 12, to: 17, key: 'portal.common.greetings.good-afternoon' },
            { from: 18, to: 22, key: 'portal.common.greetings.good-evening' },
            { from: 23, to: 23, key: 'portal.common.greetings.good-night' },
        ];
        const hr = new Date().getHours();
        const foundGreeting = greetings.find(g => hr >= g.from && hr <= g.to);

        return foundGreeting ? foundGreeting.key : 'common.hello';
    }

    public onRequestHelp() {
        this.ref = this.dialogService.open(HelpModalComponent, {
            showHeader: false,
            closable: true,
            width: '75%',
            position: 'center',
            contentStyle: {
                padding: 0,
            },
        });
    }

    ngOnDestroy() {
        if (this.ref) {
            this.ref.close();
        }
    }
}
